/**
 * Custom error class thrown when a chunk load fails due to a new deployment.
 * The SectionLoader error boundary can detect this and show a friendly
 * "new version available — please refresh" UI instead of a generic error.
 */
export class ChunkStaleError extends Error {
  constructor(sectionName?: string) {
    super(
      sectionName
        ? `Section "${sectionName}" has been updated. Please refresh to load the latest version.`
        : "A new version has been deployed. Please refresh to load the latest features."
    )
    this.name = "ChunkStaleError"
  }
}

/**
 * Retry a dynamic import once after clearing caches.
 *
 * After a new deploy the old index.html still references chunk hashes that
 * no longer exist on the server. The first import() fails with a network /
 * TypeError. We wipe the SW caches (which may hold the stale index.html)
 * and retry once.
 *
 * If the retry also fails, we throw a ChunkStaleError so the UI can show
 * a friendly "please refresh" message instead of a white screen.
 */
const importWithRetry = <T>(loader: () => Promise<T>): Promise<T> =>
  loader().catch(async (err) => {
    // Only retry on network-level failures (ChunkLoadError, TypeError, etc.)
    const msg = String(err?.message ?? "")
    const isChunkError =
      err?.name === "ChunkLoadError" ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Importing a module script failed") ||
      msg.includes("error loading dynamically imported module")

    if (!isChunkError) throw err

    console.warn("[importWithRetry] Chunk load failed, clearing caches and retrying…", err)

    // Bust SW caches so the next navigation gets a fresh index.html
    if ("caches" in window) {
      const names = await caches.keys()
      await Promise.all(names.map((n) => caches.delete(n)))
    }

    // One retry — if this succeeds, great
    try {
      return await loader()
    } catch (retryErr) {
      // Retry also failed — a new version was deployed and the old chunk
      // no longer exists. Throw a typed error so the UI can show a
      // friendly refresh prompt instead of a generic error.
      console.warn("[importWithRetry] Retry failed — new version deployed")
      throw new ChunkStaleError()
    }
  })

// Lazy load section createSection functions
export const sections = {
  assets: () => importWithRetry(() => import("@hydrologiq/assets/createSection")),
  customers: () => importWithRetry(() => import("@hydrologiq/customers/createSection")),
  systems: () => importWithRetry(() => import("@hydrologiq/systems/createSection")),
  projects: () => importWithRetry(() => import("@hydrologiq/deployments/createSection")),
  orders: () => importWithRetry(() => import("@hydrologiq/orders/createSection")),
  filling: () => importWithRetry(() => import("@hydrologiq/filling/createSection")),
  alerts: () => importWithRetry(() => import("@hydrologiq/alerts/createSection")),
  dashboards: () => importWithRetry(() => import("@hydrologiq/dashboards/createSection")),
  planner: () => importWithRetry(() => import("@hydrologiq/planner/createSection")),
  connections: () => importWithRetry(() => import("@hydrologiq/connections/createSection")),
  // planner and designer will be added when available
}

export type SectionKey = keyof typeof sections

/**
 * Prefetch all section chunks in the background so they're cached before
 * a new deploy removes the old hashed files from the server.
 *
 * This runs after the app is idle — it doesn't block initial render or
 * navigation. Once cached (by the SW's CacheFirst strategy or browser HTTP
 * cache), the chunks remain available for the lifetime of this session even
 * if a new deploy replaces them on the server.
 *
 * Call this once after the app has finished its initial load.
 */
export const prefetchAllSections = () => {
  const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 2000))

  idle(() => {
    Object.keys(sections).forEach((key) => {
      const loader = sections[key as SectionKey]
      // Fire-and-forget — errors are swallowed since this is best-effort
      loader().catch(() => {
        // Prefetch failed for this section — not critical, importWithRetry
        // will handle it when the user actually navigates there.
      })
    })
  })
}
